/**
 * USB scanners commonly emulate a US keyboard. `event.key` is translated by
 * the active input language, while `event.code` remains the physical key.
 * Rebuild only fast scanner bursts from `code` so typed punctuation and Thai
 * text remain untouched.
 */

const UNSHIFTED: Record<string, string> = {
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  Space: ' ',
};

const SHIFTED: Record<string, string> = {
  Backquote: '~',
  Minus: '_',
  Equal: '+',
  BracketLeft: '{',
  BracketRight: '}',
  Backslash: '|',
  Semicolon: ':',
  Quote: '"',
  Comma: '<',
  Period: '>',
  Slash: '?',
  Digit1: '!',
  Digit2: '@',
  Digit3: '#',
  Digit4: '$',
  Digit5: '%',
  Digit6: '^',
  Digit7: '&',
  Digit8: '*',
  Digit9: '(',
  Digit0: ')',
};

export interface ScannerKeyEvent {
  code: string;
  shiftKey: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  timeStamp: number;
}

export const POS_QUANTITY_COMMAND_PREFIX = 'QTY';
export const POS_QUANTITY_COMMAND_MAX = 9_999;

export interface QuantityCommandBarcodeConflict {
  barcodeId: string;
  barcode: string;
  productId: string;
  productName: string;
  productUnitId: string;
  productUnitName: string;
}

export type QuantityCommandResult =
  | { kind: 'not-command' }
  | {
      kind: 'invalid';
      normalized: string;
      reason: 'format' | 'range';
    }
  | {
      kind: 'quantity';
      normalized: string;
      quantity: number;
    };

const normalizeCommandValue = (value: string): string =>
  value.trim().normalize('NFKC').toUpperCase();

export const isReservedProductBarcode = (value: string): boolean =>
  normalizeCommandValue(value).startsWith(POS_QUANTITY_COMMAND_PREFIX);

export const parseQuantityCommand = (value: string): QuantityCommandResult => {
  const normalized = normalizeCommandValue(value);
  if (!normalized.startsWith(POS_QUANTITY_COMMAND_PREFIX)) {
    return { kind: 'not-command' };
  }
  const match = /^QTY(\d{5})$/.exec(normalized);
  if (!match) {
    return { kind: 'invalid', normalized, reason: 'format' };
  }
  const quantity = Number(match[1]);
  if (quantity < 1 || quantity > POS_QUANTITY_COMMAND_MAX) {
    return { kind: 'invalid', normalized, reason: 'range' };
  }
  return { kind: 'quantity', normalized, quantity };
};

export const isLikelyManualBarcode = (value: string): boolean =>
  /^\d{6,}$/.test(value.trim());

const fromUsKeyboardCode = (event: ScannerKeyEvent): string | null => {
  if (event.altKey || event.ctrlKey || event.metaKey) return null;
  if (/^Key[A-Z]$/.test(event.code)) {
    const value = event.code.slice(3).toLowerCase();
    return event.shiftKey ? value.toUpperCase() : value;
  }
  if (/^Digit[0-9]$/.test(event.code)) {
    return event.shiftKey ? SHIFTED[event.code]! : event.code.slice(5);
  }
  return (event.shiftKey ? SHIFTED : UNSHIFTED)[event.code] ?? null;
};

export class ScannerBurstNormalizer {
  private readonly values: Array<{ value: string; at: number }> = [];

  constructor(
    private readonly maxGapMs = 45,
    private readonly minLength = 4,
  ) {}

  push(event: ScannerKeyEvent): void {
    const value = fromUsKeyboardCode(event);
    const previous = this.values.at(-1);
    if (!value) {
      this.reset();
      return;
    }
    if (previous && event.timeStamp - previous.at > this.maxGapMs) {
      this.reset();
    }
    this.values.push({ value, at: event.timeStamp });
  }

  complete(event: Pick<ScannerKeyEvent, 'timeStamp'>): string | null {
    const previous = this.values.at(-1);
    const isBurst =
      this.values.length >= this.minLength &&
      previous !== undefined &&
      event.timeStamp - previous.at <= this.maxGapMs;
    const value = isBurst
      ? this.values.map((entry) => entry.value).join('')
      : null;
    this.reset();
    return value;
  }

  reset(): void {
    this.values.length = 0;
  }
}
